import {
  BreakpointQueryName,
  ThemedFunctor,
} from '@doveit/bricks';
import { css } from 'styled-components';
import { SimplePageLayoutWrapperSize } from './types';

const sizeMap: Record<SimplePageLayoutWrapperSize, string> = {
  S: '40rem',
  M: '60rem',
  L: '86rem',
  FULL: '100%',
};

export const base: ThemedFunctor = ({ theme }) => css`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${theme.color.background.neutral.default.lowest};
`;

export const content: ThemedFunctor = ({
  theme,
  $fitMaxAvailableHeight,
}) => css`
  flex-grow: 1;
  position: relative;
  display: flex;

  ${$fitMaxAvailableHeight ? `
    height: 100%;
  ` : `
    /** Without a minimum height, browsers don't calculate the right height */
    min-height: 7.5rem;
  `}

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    background-color: ${theme.color.background.neutral.default.low};
  }
`;

export const main: ThemedFunctor = ({ theme, $fitMaxAvailableHeight }) => css`
  position: relative;
  overflow-y: scroll;
  scroll-behavior: smooth;
  overflow-x: hidden;
  flex: 1;
  padding: ${theme.remSize[200]} 0 ${theme.remSize[1000]};

  ${$fitMaxAvailableHeight ? `
    height: 100%;
  ` : `
    /** Without a minimum height, browsers don't calculate the right height */
    min-height: 7.5rem;
  `}

  &::-webkit-scrollbar {
    width: 0;
  }

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    padding: ${theme.remSize[400]} 0;
  }
`;

export const frame: ThemedFunctor = ({ theme }) => css`
  padding: 0 ${theme.remSize[300]} ${theme.remSize[300]};
  margin: 0 -${theme.remSize[300]} ${theme.remSize[300]};
  background-color: ${theme.palettes.neutrals.lightest};

  &:last-child {
    margin-bottom: 0;
  }

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    margin: 0 0 ${theme.remSize[200]};
    border: 1px solid ${theme.color.border.neutral.default.low};
    padding: ${theme.remSize[300]};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const wrapper: ThemedFunctor = ({
  theme,
  $size,
  $fitMaxAvailableHeight,
}) => css`
  padding: 0 ${theme.remSize[200]};
  max-width: ${sizeMap[$size as SimplePageLayoutWrapperSize]};
  margin: 0 auto;

  ${$fitMaxAvailableHeight && `
    &, > * {
      height: 100%;
    }
  `}

  ${theme.breakpoints.up(BreakpointQueryName.MD)} {
    padding: 0 ${theme.remSize[200]};
  }
`;

export const header: ThemedFunctor = ({ $hideHeader, theme }) => css`
  position: relative;
  padding: ${theme.remSize[150]} 0;
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition: grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out;;
  z-index: ${theme.zIndex.navbar};
  background-color: ${theme.color.background.neutral.default.lowest};
  border-bottom: 1px solid ${theme.palettes.neutrals.light};

  ${$hideHeader && `
    grid-template-rows: 0fr;
    opacity: 0;
    padding: 0;

    > * {
      overflow: hidden;
    }
  `}
`;

export const bar: ThemedFunctor = ({
  theme,
}) => css`
  position: relative;
  padding: ${theme.remSize[150]} 0;
  z-index: ${theme.zIndex.navbar};
  background-color: ${theme.color.background.neutral.default.lowest};
  position: relative;
  border-top: 1px solid ${theme.palettes.neutrals.light};
  border-bottom: 1px solid ${theme.palettes.neutrals.light};
  margin-top: -1px;

  &:hover {
    /**
      The element must have a z-index that:
      - Exceeds the main navigation bar's z-index (6003) to ensure tooltips or floating elements display correctly.
      - Remains lower than the z-index of the side navigation.

      But, keep in mind that the main navigation should always have a higher z-index than the side navigation.

      This is why the z-index for this element dynamically changes on hover
    */
    z-index: ${theme.zIndex.navbar + 4};
  }
`;

export const backToTop: ThemedFunctor = ({ theme, $visible }) => css`
  width: ${theme.remSize[600]};
  height: ${theme.remSize[600]};
  border-radius: ${theme.remRounding.M};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.color.foreground.neutral.default.lowest};
  background-color: ${theme.palettes.brand.primary};
  position: fixed;
  bottom: ${theme.remSize[200]};
  right: ${theme.remSize[200]};
  opacity: 0;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  visibility: hidden;
  z-index: ${theme.zIndex.navbar};

  ${$visible && `
    opacity: 1;
    visibility: visible;
  `}
`;
